import type { AnswerMap } from "./answerMap";
import { isTrue } from "./answerMap";

export type ArticleSlotId =
  | "symptoms"
  | "menstruation"
  | "gut"
  | "diet"
  | "exercise"
  | "supplements"
  | "women_iron_needs"
  | "postpartum"
  | "ferritin_fertility"
  | "life_stage_pending"
  | "thyroid"
  | "celiac"
  | "inflammatory_bowel"
  | "endometriosis"
  | "chronic_inflammation"
  | "pregnancy_contraception"
  | "blood_donation";

export interface ArticleSlot {
  id: ArticleSlotId;
  kind: "fixed" | "routed";
}

export type ArticleRule =
  | { rule: "fixed"; slot: Extract<ArticleSlotId, "menstruation" | "gut" | "diet" | "exercise" | "supplements"> }
  | { rule: "life_stage" }
  | { rule: "conditions" }
  | { rule: "pregnancy" }
  | { rule: "blood_donation" };

export const LIFE_STAGE_PRIORITY = [
  "pregnant",
  "gave_birth_within_last_year",
  "fertility_treatment",
  "trying_to_conceive",
  "planning_pregnancy",
  "perimenopause",
  "menopause",
  "postmenopause",
  "menstruating",
] as const;

const LIFE_STAGE_SLOT: Record<(typeof LIFE_STAGE_PRIORITY)[number], ArticleSlotId> = {
  pregnant: "women_iron_needs",
  gave_birth_within_last_year: "postpartum",
  fertility_treatment: "ferritin_fertility",
  trying_to_conceive: "ferritin_fertility",
  planning_pregnancy: "ferritin_fertility",
  perimenopause: "life_stage_pending",
  menopause: "life_stage_pending",
  postmenopause: "life_stage_pending",
  menstruating: "women_iron_needs",
};

const CONDITION_SLOT: Record<string, ArticleSlotId | undefined> = {
  hypothyroidism: "thyroid",
  autoimmune_thyroiditis: "thyroid",
  celiac_disease: "celiac",
  crohns_disease: "inflammatory_bowel",
  endometriosis: "endometriosis",
  rheumatic_disease: "chronic_inflammation",
};

const PREGNANCY_KEYS = ["child_birth", "miscarriage", "contraceptives"] as const;

export function lifeStageArticle(answers: AnswerMap): ArticleSlot[] {
  const stages = answers.female_reproductive_stage;
  if (stages?.kind !== "multi_choice") {
    return [];
  }
  const stage = LIFE_STAGE_PRIORITY.find((candidate) => stages.value.includes(candidate));
  return stage ? [{ id: LIFE_STAGE_SLOT[stage], kind: "routed" }] : [];
}

export function conditionArticles(answers: AnswerMap): ArticleSlot[] {
  const conditions = answers.medical_conditions_details;
  if (conditions?.kind !== "multi_choice") {
    return [];
  }
  const ids = new Set<ArticleSlotId>();
  for (const condition of conditions.value) {
    const id = CONDITION_SLOT[condition];
    if (id) {
      ids.add(id);
    }
  }
  return [...ids].map((id) => ({ id, kind: "routed" }));
}

export function pregnancyArticle(answers: AnswerMap): ArticleSlot[] {
  return PREGNANCY_KEYS.some((key) => isTrue(answers, key)) ? [{ id: "pregnancy_contraception", kind: "routed" }] : [];
}

export function bloodDonationArticle(answers: AnswerMap): ArticleSlot[] {
  return isTrue(answers, "blood_donation") ? [{ id: "blood_donation", kind: "routed" }] : [];
}

export function articlesFor(rule: ArticleRule, answers: AnswerMap): ArticleSlot[] {
  switch (rule.rule) {
    case "fixed":
      return [{ id: rule.slot, kind: "fixed" }];
    case "life_stage":
      return lifeStageArticle(answers);
    case "conditions":
      return conditionArticles(answers);
    case "pregnancy":
      return pregnancyArticle(answers);
    case "blood_donation":
      return bloodDonationArticle(answers);
  }
}
